// MissedCallNotifications.js - Missed Call Notifications Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellOff, Phone, PhoneOff, Check, TrendingUp, TrendingDown, Settings as SettingsIcon } from 'lucide-react';
import apiService from './apiService';
import io from 'socket.io-client';

const MissedCallNotifications = ({ token, agentSip, darkMode, onCallBack }) => {
  const [missedCalls, setMissedCalls] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState(null);
  const [queueStats, setQueueStats] = useState([]);
  const [preferences, setPreferences] = useState({
    enabled: true,
    browser_notifications: true,
    sound_enabled: true,
    auto_mark_read_after: 3600,
    notify_on_break: false
  });
  
  // Filters
  const [filters, setFilters] = useState({
    unread_only: false,
    queue: '',
    answered_filter: 'all', // 'all', 'answered', 'abandoned'
    days: 7
  });

  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const panelRef = useRef(null);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!token) return;

    // Use BACKEND_URL (without /api) for WebSocket, fallback to current origin
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    
    console.log('🔌 Connecting to WebSocket:', backendUrl);

    // FIX: Use polling only (WebSocket upgrade fails but polling works fine)
    const socket = io(backendUrl, {
      path: '/socket.io/',
      transports: ['polling'], // Use polling only - reliable and works everywhere
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 20000,
      autoConnect: true,
      forceNew: false,
      upgrade: false // Don't try to upgrade to WebSocket
    });

    socket.on('connect', () => {
      console.log('🔌 WebSocket connected');
      socket.emit('authenticate', token);
    });

    socket.on('authenticated', (data) => {
      if (data.success) {
        console.log('✅ WebSocket authenticated');
      }
    });

    socket.on('new_missed_call', (data) => {
      console.log('📞 New missed call:', data);
      
      // Add to list
      setMissedCalls(prev => [data, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification
      if (preferences.browser_notifications && Notification.permission === 'granted') {
        new Notification('Missed Call', {
          body: `From ${data.caller_name || data.caller_number} in ${data.queue_name}`,
          tag: `missed-call-${data.id}`
          // Note: icon removed - add /public/phone-icon.png if you want an icon
        });
      }
      
      // Play sound (only if audio file exists)
      if (preferences.sound_enabled && audioRef.current) {
        audioRef.current.play().catch(e => {
          // Silently fail if sound file doesn't exist
          console.log('Notification sound not available');
        });
      }
    });

    socket.on('unread_count', (data) => {
      setUnreadCount(data.count);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
    });

    // FIX: Add error handlers for debugging
    socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error.message);
    });

    socket.on('connect_timeout', () => {
      console.error('❌ WebSocket connection timeout');
    });

    socket.on('error', (error) => {
      console.error('❌ WebSocket error:', error);
    });

    socketRef.current = socket;

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [token, preferences]);

  // Request browser notification permission
  useEffect(() => {
    if (preferences.browser_notifications && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [preferences.browser_notifications]);

  // Fetch missed calls
  const fetchMissedCalls = useCallback(async () => {
    try {
      const params = {
        unread_only: filters.unread_only,
        limit: 50,
        ...(filters.queue && { queue: filters.queue }),
        ...(filters.answered_filter !== 'all' && { answered_filter: filters.answered_filter })
      };

      const response = await apiService.getMissedCalls(params);
      setMissedCalls(response.missedCalls || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch missed calls:', error);
    }
  }, [filters.unread_only, filters.queue, filters.answered_filter]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getMissedCallStats(filters.days);
      setStats(response);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [filters.days]);

  // Fetch queue breakdown
  const fetchQueueStats = useCallback(async () => {
    try {
      const response = await apiService.getMissedCallsByQueue(filters.days);
      setQueueStats(response);
    } catch (error) {
      console.error('Failed to fetch queue stats:', error);
    }
  }, [filters.days]);

  // Fetch preferences
  const fetchPreferences = async () => {
    try {
      const response = await apiService.getNotificationPreferences();
      setPreferences(response);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  // Update preferences
  const updatePreferences = async (newPrefs) => {
    try {
      const response = await apiService.updateNotificationPreferences(newPrefs);
      setPreferences(response);
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  };

  // Mark as read
  const markAsRead = async (id) => {
    try {
      await apiService.markMissedCallRead(id);
      setMissedCalls(prev => prev.map(call => 
        call.id === id ? { ...call, read: true } : call
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await apiService.markAllMissedCallsRead();
      setMissedCalls(prev => prev.map(call => ({ ...call, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Call back
  const callBack = async (id, number) => {
    try {
      // Mark as read first
      await markAsRead(id);
      
      // Use the phone dialer callback if provided
      if (onCallBack) {
        onCallBack(number);
      } else {
        // Fallback: Copy to clipboard
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(number);
          alert(`Number copied to clipboard: ${number}\n\nPlease dial from your phone.`);
        } else {
          alert(`Please call: ${number}`);
        }
      }
    } catch (error) {
      console.error('Callback error:', error);
      alert(`Error initiating call. Please dial manually: ${number}`);
    }
  };

  // Initial load
  useEffect(() => {
    if (token) {
      fetchMissedCalls();
      fetchPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters]);

  // Load stats when panel opens
  useEffect(() => {
    if (showStats && token) {
      fetchStats();
      fetchQueueStats();
    }
  }, [showStats, token, filters.days, fetchStats, fetchQueueStats]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPanel]);

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-white' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className="relative" ref={panelRef}>
      {/* Notification Sound */}
      <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />

      {/* Bell Icon with Badge */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className={`relative p-2 rounded-lg hover:bg-opacity-10 hover:bg-gray-500 transition-colors ${textColor}`}
      >
        {preferences.enabled ? <Bell className="w-6 h-6" /> : <BellOff className="w-6 h-6" />}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <div className={`absolute right-0 mt-2 w-96 ${bgColor} ${textColor} ${borderColor} border rounded-lg shadow-2xl z-50 max-h-[600px] flex flex-col`}>
          {/* Header */}
          <div className={`p-4 border-b ${borderColor} flex justify-between items-center`}>
            <h3 className="font-bold text-lg">Missed Calls</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setShowStats(!showStats)}
                className="p-1 hover:bg-gray-600 rounded"
                title="Statistics"
              >
                <TrendingUp className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1 hover:bg-gray-600 rounded"
                title="Settings"
              >
                <SettingsIcon className="w-5 h-5" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className={`p-4 border-b ${borderColor}`}>
              <h4 className="font-semibold mb-3">Notification Settings</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={preferences.enabled}
                    onChange={(e) => updatePreferences({ ...preferences, enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Enable notifications</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={preferences.browser_notifications}
                    onChange={(e) => updatePreferences({ ...preferences, browser_notifications: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Browser notifications</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={preferences.sound_enabled}
                    onChange={(e) => updatePreferences({ ...preferences, sound_enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Sound alerts</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={preferences.notify_on_break}
                    onChange={(e) => updatePreferences({ ...preferences, notify_on_break: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Notify when on break</span>
                </label>
              </div>
            </div>
          )}

          {/* Statistics Panel */}
          {showStats && stats && (
            <div className={`p-4 border-b ${borderColor}`}>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold">Statistics (Last {filters.days} days)</h4>
                <select
                  value={filters.days}
                  onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                  className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                >
                  <option value="1">1 day</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-gray-400 text-xs">Total Missed</div>
                  <div className="text-xl font-bold">{stats.total_missed}</div>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-gray-400 text-xs">Answered by Others</div>
                  <div className="text-xl font-bold text-green-500">{stats.answered_by_others}</div>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-gray-400 text-xs">Abandoned</div>
                  <div className="text-xl font-bold text-red-500">{stats.abandoned}</div>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-gray-400 text-xs">Avg Ring Time</div>
                  <div className="text-xl font-bold">{Math.round(stats.avg_ring_duration)}s</div>
                </div>
              </div>

              <div className={`mt-3 p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Team Average: {stats.team_average.toFixed(1)}</span>
                  {stats.performance === 'above_average' ? (
                    <span className="text-green-500 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" /> Above Average
                    </span>
                  ) : (
                    <span className="text-red-500 flex items-center gap-1">
                      <TrendingDown className="w-4 h-4" /> Below Average
                    </span>
                  )}
                </div>
              </div>

              {/* Queue Breakdown */}
              {queueStats.length > 0 && (
                <div className="mt-3">
                  <h5 className="text-sm font-semibold mb-2">By Queue</h5>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {queueStats.map(q => (
                      <div key={q.queue_name} className={`p-2 rounded text-xs ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <div className="flex justify-between">
                          <span className="font-medium">{q.queue_name}</span>
                          <span>{q.missed_count} missed</span>
                        </div>
                        <div className="text-gray-400 flex gap-2">
                          <span>✓ {q.answered_count}</span>
                          <span>✗ {q.abandoned_count}</span>
                          {q.unread_count > 0 && <span className="text-red-500">• {q.unread_count} unread</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className={`p-3 border-b ${borderColor} flex gap-2 flex-wrap text-sm`}>
            <button
              onClick={() => setFilters({ ...filters, unread_only: !filters.unread_only })}
              className={`px-3 py-1 rounded ${filters.unread_only ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              Unread Only
            </button>
            <select
              value={filters.answered_filter}
              onChange={(e) => setFilters({ ...filters, answered_filter: e.target.value })}
              className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              <option value="all">All</option>
              <option value="answered">Answered</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>

          {/* Missed Calls List */}
          <div className="flex-1 overflow-y-auto">
            {missedCalls.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No missed calls</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {missedCalls.map(call => (
                  <div
                    key={call.id}
                    className={`p-3 hover:bg-opacity-5 hover:bg-gray-500 transition-colors ${!call.read ? 'bg-blue-900 bg-opacity-20' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${call.answered ? 'bg-green-900' : call.abandoned ? 'bg-red-900' : 'bg-gray-700'}`}>
                        {call.answered ? <Phone className="w-4 h-4 text-green-400" /> : <PhoneOff className="w-4 h-4 text-red-400" />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold">
                              {call.caller_name || call.caller_number}
                            </div>
                            {call.caller_name && (
                              <div className="text-sm text-gray-400">{call.caller_number}</div>
                            )}
                          </div>
                          {!call.read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-400 mt-1">
                          {call.queue_name} • {call.time_ago}
                        </div>
                        
                        {call.answered && (
                          <div className="text-sm text-green-400 mt-1">
                            ✓ Answered by {call.answered_by_name || call.answered_by}
                          </div>
                        )}
                        
                        {call.abandoned && (
                          <div className="text-sm text-red-400 mt-1">
                            ✗ Call abandoned
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => callBack(call.id, call.caller_number)}
                            className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1"
                          >
                            <Phone className="w-3 h-3" /> Call Back
                          </button>
                          {!call.read && (
                            <button
                              onClick={() => markAsRead(call.id)}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" /> Mark Read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MissedCallNotifications;
